import React, { useEffect, useState } from 'react';
import { Add as AddIcon } from '@mui/icons-material';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import RemoveIcon from '@mui/icons-material/Remove';
import { Divider, ListItemIcon, MenuItem, Popover, TextField, Typography } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { CustomAutoSelectFolderProps } from './types';
import { FolderToSelect } from '../../shared.types';

const CustomAutoSelectFolder = ({
  inputProps,
  data: dataParam,
  onChange,
  isAddNewFolderAtBottom,
  setOpenFolderDialog,
  newFolder,
  helperText,
  formValues,
}: CustomAutoSelectFolderProps) => {
  const { id, name, label } = inputProps;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [popOverWidth, setPopOverWidth] = useState<number>();
  const [data, setData] = useState<FolderToSelect[]>(dataParam);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    const anchorElement = event.currentTarget;
    setAnchorEl(anchorElement);
    setPopOverWidth(anchorElement.offsetWidth);
  };

  const handleOpenDialog = () => {
    if (setOpenFolderDialog) {
      setOpenFolderDialog(true);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const popOverId = open ? 'custom-auto-select-popover' : undefined;

  const renderTree = (nodes: FolderToSelect) => {
    if (nodes.id === undefined || nodes.id === null) {
      return null;
    }
    const nodeId = nodes.id.toString();
    return (
      <TreeItem key={nodeId} itemId={nodeId} label={nodes.name}>
        {Array.isArray(nodes.subFolders)
          ? nodes.subFolders.filter(node => node.id !== undefined && node.id !== null).map(node => renderTree(node))
          : null}
      </TreeItem>
    );
  };

  const handleSelect = (event: React.SyntheticEvent<Element, Event> | null, itemId: string | null) => {
    const selectedFolder = itemId ?? '0';
    onChange(selectedFolder);

    const target = event?.target as HTMLElement;
    setSelectedOption(target?.innerText ?? '');

    handleClose();
  };

  useEffect(() => {
    if (newFolder) {
      const newData = [...data, newFolder as FolderToSelect];
      if (newFolder.id) {
        onChange(newFolder.id.toString());
        setSelectedOption(newFolder.name);
        if (setOpenFolderDialog) {
          setOpenFolderDialog(false);
        }
        // if the new folder is a subfolder, it should be added to the data array as a subfolder
        if (newFolder.parent_folder) {
          const parentFolder = newData.find(folder => folder.id === newFolder.parent_folder.id);
          if (parentFolder) {
            parentFolder.subFolders = [...(parentFolder.subFolders || []), newFolder as FolderToSelect];
            // remove the new folder from the data array
            const newDataWithoutNewFolder = newData.filter(folder => folder.id !== newFolder.id);
            setData(newDataWithoutNewFolder);
          }
        } else {
          setData(newData);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newFolder]);

  useEffect(() => {
    if (formValues?.folder) {
      const findFolder = (folders: FolderToSelect[], folderId: number): FolderToSelect | undefined => {
        let foundFolder: FolderToSelect | undefined;
        folders.some(folder => {
          if (folder.id === folderId) {
            foundFolder = folder;
            return true;
          }
          if (folder.subFolders) {
            foundFolder = findFolder(folder.subFolders, folderId);
            return foundFolder !== undefined;
          }
          return false;
        });
        return foundFolder;
      };

      const selectedFolder = findFolder(data, formValues.folder);
      if (selectedFolder) {
        setSelectedOption(selectedFolder.name);
      }
    }
  }, [formValues?.folder, data]);

  return (
    <>
      <TextField
        fullWidth
        id={id}
        name={name}
        placeholder={label}
        variant="outlined"
        onClick={handleClick}
        value={selectedOption}
        helperText={helperText ?? ''}
        className="custom-tree-container"
      />
      <Popover
        id={popOverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            style: { width: popOverWidth },
          },
        }}>
        <SimpleTreeView
          className="custom-tree-container"
          slots={{
            collapseIcon: IndeterminateCheckBoxOutlinedIcon,
            expandIcon: AddBoxOutlinedIcon,
            endIcon: RemoveIcon,
          }}
          onSelectedItemsChange={handleSelect}>
          {data.map(item => renderTree(item))}
          {isAddNewFolderAtBottom && (
            <div className="add-new-folder-button">
              <Divider />
              <MenuItem onClick={handleOpenDialog}>
                <ListItemIcon>
                  <AddIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="inherit">Add new folder</Typography>
              </MenuItem>
            </div>
          )}
        </SimpleTreeView>
      </Popover>
    </>
  );
};

export default CustomAutoSelectFolder;
